import { useState, useEffect } from "react";
import * as cx from "classnames/bind";
import m from "styles/Modal.module.scss";
import s from "styles/admin.module.scss";
import { Icon, Button, Modal } from "components";
import Link from "next/link";
import { useRouter } from "next/router";
import { useHorecaBasePath } from "hooks";
import { useTranslation } from "next-i18next";
import sortBy from "lodash/sortBy";
import uniqBy from "lodash/uniqBy";

const ManagerModal = ({ user, show, onClose, ...other }) => {
  const router = useRouter();
  const organizations = uniqBy(
    (user?.organizations || []).map((o) => o?.organization || o),
    "id",
  );
  const bars = uniqBy(
    user?.managedBars?.length > 0 ? user?.managedBars : user?.tended_bars || [],
    "id",
  );
  const hotels = uniqBy(
    user?.managedHotels?.length > 0 ? user?.managedHotels : [],
    "id",
  );
  const allUnits = [
    ...organizations.map((o) => ({ ...o, type: "organization" })),
    ...bars.map((b) => ({ ...b, type: "bar" })),
    ...hotels.map((h) => ({ ...h, type: "hotel" })),
  ];
  const { t } = useTranslation();

  const [managedUnit, setManagedUnit] = useState(
    other?.bar?.id
      ? `manage-bar-${other.bar.id}`
      : other?.hotel?.id
      ? `manage-hotel-${other.hotel.id}`
      : `manage-organization-${other?.organization?.id}`,
  );

  const handleChange = (e) => {
    setManagedUnit(e.target.value);
  };

  const selected = allUnits.find(
    (u) => `manage-${u.type || "organization"}-${u.id}` === managedUnit,
  );

  const onButtonClick = (e) => {
    e.preventDefault();
    const { pathname } = router;
    const query = {
      organization: selected?.organization?.slug || selected?.slug,
      ...(selected.type == "bar"
        ? selected?.hotel?.id
          ? { bar: selected.slug, hotel: selected.hotel.slug }
          : { bar: selected.slug }
        : selected.type == "hotel"
        ? { hotel: selected.slug }
        : {}),
    };

    const replacement = sortBy(Object.entries(query), ([k, v]) =>
      k == "organization" ? 0 : k == "hotel" ? 1 : 2,
    )
      .map(([k, v]) => `${k != "organization" ? `${k}s/` : ""}[${k}]`)
      .join("/");
    const path = pathname.replace(/(\[\w+\](\/\w+\/)?)+/, replacement);
    router.push({ pathname: path, query });
    onClose();
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      title="Select unit to manage"
      className={cx(m.mobileBottom, "mobileBottom")}
    >
      <form>
        {user?.organizationManager && user?.organizations?.length > 0 ? (
          <>
            <label>
              <small>
                <b>Manage organization:</b>
              </small>
            </label>
            <ul>
              {organizations.map((organization) => {
                const inputId = `manage-organization-${organization.id}`;
                return (
                  <li key={organization.id}>
                    <label htmlFor={inputId}>
                      <input
                        type="radio"
                        name="manageUnit"
                        id={inputId}
                        value={inputId}
                        checked={managedUnit === inputId}
                        onChange={handleChange}
                      />
                      <span className="ml8">{organization.name}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
            <div className="or-divider">OR</div>
          </>
        ) : (
          <></>
        )}

        {hotels.length > 0 ? (
          <>
            <label>
              <small>
                <b>Manage hotels:</b>
              </small>
            </label>
            <ul>
              {hotels.map((hotel) => {
                const inputId = `manage-hotel-${hotel.id}`;
                return (
                  <li key={hotel.id}>
                    <label htmlFor={inputId}>
                      <input
                        type="radio"
                        name="manageUnit"
                        id={inputId}
                        value={inputId}
                        checked={managedUnit === inputId}
                        onChange={handleChange}
                      />
                      <span className="ml8">{hotel.name}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
            <div className="or-divider">OR</div>
          </>
        ) : (
          <></>
        )}

        {bars.length > 0 ? (
          <>
            <label>
              <small>
                <b>Manage bars:</b>
              </small>
            </label>
            <ul>
              {bars.map((bar) => {
                const inputId = `manage-bar-${bar.id}`;
                return (
                  <li key={bar.id}>
                    <label htmlFor={inputId}>
                      <input
                        type="radio"
                        name="manageUnit"
                        id={inputId}
                        value={inputId}
                        checked={managedUnit === inputId}
                        onChange={handleChange}
                      />
                      <span className="ml8">{bar.name}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <></>
        )}

        <div className={cx(m.ModalFooterSticky)}>
          <div className="ButtonGroup flex">
            <Button
              className={cx(
                "Primary Button Gold flex-grow mb00",
                !selected && "disabled",
              )}
              disabled={!selected && true}
              onClick={onButtonClick}
            >
              {selected ? `Manage ${selected.name}` : "Selected a unit above"}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

const Breadcrumbs = ({ setShowManager, ...other }) => {
  const iconMap = {
    organization: "sitemap",
    hotel: "hotel",
    bar: "wine-bottle",
  };
  const keys = ["organization", "hotel", "bar"];
  const breadcrumbs = keys
    .map((k) => [k, other[k]])
    .filter(([_, o]) => o)
    .map(([k, o]) => [k, Array.isArray(o) ? o[0] : o]);

  const lastBreadcrumb = breadcrumbs[breadcrumbs.length - 1];

  const breadcrumbsReversed = [...breadcrumbs].reverse();

  return (
    <div className={cx(s.horecaBreadcrumbs)}>
      <b>You are managing:</b>
      <div className="flex flex-wrap">
        {breadcrumbsReversed.map(([k, o]) => {
          if (!o?.name) return null; // Prevents rendering empty items

          const href =
            k === "organization"
              ? {
                  pathname: "/horeca/[organization]",
                  query: { organization: o?.slug },
                }
              : k === "hotel"
              ? {
                  pathname: "/horeca/[organization]/hotels/[hotel]",
                  query: {
                    organization: other?.organization?.slug,
                    hotel: o?.slug,
                  },
                }
              : k === "bar"
              ? other.hotel
                ? {
                    pathname:
                      "/horeca/[organization]/hotels/[hotel]/bars/[bar]",
                    query: {
                      organization: other?.organization?.slug,
                      hotel: other?.hotel?.slug,
                      bar: o?.slug,
                    },
                  }
                : {
                    pathname: "/horeca/[organization]/bars/[bar]",
                    query: {
                      organization:
                        other?.organization?.slug || o?.organization?.slug,
                      bar: o?.slug,
                    },
                  }
              : {};

          return (
            <span key={k} className="">
              {/*
              <Link href={href} passHref a className="flex items-center">
                <Icon size="far" svg={iconMap[k]} className="mr-2" />
                {o.name}
              </Link>
              */}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowManager(true);
                }}
                a
                className="flex items-center"
              >
                <Icon size="far" svg={iconMap[k]} className="mr-2" />
                {o.name}
              </a>
            </span>
          );
        })}
        <a
          className="Button Green Small RadA"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setShowManager(true);
          }}
        >
          EDIT
        </a>
      </div>
    </div>
  );
};

const HorecaContent = ({ children, title, description, ...other }) => {
  const { t } = useTranslation();
  const user = other.user;

  const bar = user?.availableBars.find((b) => b.id == other?.bar?.id);
  const availableBars = bar
    ? user.availableBars.filter((b) => b.id != bar.id)
    : [];

  const [showManager, setShowManager] = useState(false);

  return (
    <div className={cx("AdminContent", s.AdminContent)}>
      <ManagerModal
        {...other}
        show={showManager}
        onClose={() => setShowManager(false)}
      />

      {(user?.organizationManager ||
        ((user?.barManager || user?.hotelManager) &&
          user?.managedBars?.length > 1) ||
        (user?.hotelManager && user?.managedHotels?.length > 1) ||
        user?.tended_bars?.length > 1) && (
        <>
          <div
            className={cx("contentInner", s.contentInner)}
            style={{ paddingTop: "10px", paddingBottom: "10px" }}
          >
            <div className={cx(s.horecaBarSelector)}>
              <div className={cx(s.horecaBarSelectorSelected)}>
                <Breadcrumbs {...other} setShowManager={setShowManager} />
              </div>
            </div>
          </div>
        </>
      )}
      {/*
      {(user?.barTender || user?.barManager) && availableBars.length > 0 && (
        <div
          className={cx("contentInner", s.contentInner)}
          style={{ paddingTop: "10px", paddingBottom: "10px" }}
        >
          <div className={cx(s.horecaBarSelector)}>
            <div className={cx(s.horecaBarSelectorSelected)}>
              <Breadcrumbs {...other} setShowManager={setShowManager} />
            </div>
            <div className={cx(s.horecaBarSelectorAvailable)}>
              <b>Manage another bar:</b>
              <div className="flex flex-wrap">
                {availableBars?.map((bar) => {
                  return (
                    <span key={bar.id} className="mr-4">
                      <Link
                        href={`/horeca/${bar.url}`}
                        className="flex items-center"
                      >
                        <Icon size="far" svg="bottle" className="mr-2" />
                        {bar.name}
                      </Link>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      */}
      <div className={cx("contentInner", s.contentInner)}>
        {title && (
          <div className={cx("contentHeader", s.contentHeader)}>
            {title && <h1>{title}</h1>}
            {description && <p className="m0_reg mt8">{description}</p>}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default HorecaContent;
